import React, { useEffect, useMemo } from 'react';

import Space from 'components/generic/Space.component';
import CONF from 'config/conf.singleton';
import RELEASE from 'config/release.singleton';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import fromAnyToString from 'utils/converters/fromAnyToString';
import logger from 'utils/logger.util';

const L = logger('ReleaseInfoBox');

const ReleaseInfoBoxRow = ({ name, value }) => {
  const { t } = useTranslation();
  const v = fromAnyToString(value);

  return (
    <tr data-bem="ReleaseInfoBoxRow">
      <td data-bem="ReleaseInfoBoxRow__name">{name}:</td>
      {v ? (
        <td data-bem="ReleaseInfoBoxRow__value--set">{v}</td>
      ) : (
        <td
          data-bem="ReleaseInfoBoxRow__value--unset"
          style={{
            color: '#CCCCCC',
          }}
        >
          {t('composite.sidebarMenu.releaseInfoBox.notSet')}
        </td>
      )}
    </tr>
  );
};

const ReleaseInfoBoxBreak = () => (
  <tr data-bem="ReleaseInfoBoxBreak">
    <td colSpan={2}>
      <hr />
    </td>
  </tr>
);

const ReleaseInfoBox = ({ extended }) => {
  const [display, setDisplay] = useStateWhileMounted('');
  const [nextSha, setNextSha] = useStateWhileMounted('');
  const [nextNow, setNextNow] = useStateWhileMounted('');
  const { val: isUpToDate, set: setIsUpToDate } = useTip(true);

  const { t } = useTranslation();

  useEffect(
    () =>
      RELEASE.subscribe(() => {
        RELEASE.display().then(setDisplay);
        RELEASE.isUpToDate().then(setIsUpToDate);
        RELEASE.nextNow().then(setNextNow);
        RELEASE.nextSha().then(setNextSha);
      }),
    [setDisplay, setIsUpToDate, setNextNow, setNextSha],
  );

  // NOTE: using memo here as to only print the message whenever the values are changed, not every component render
  useMemo(() => void L.info('()', { display, isCurrent: isUpToDate }), [display, isUpToDate]);

  if (!extended) {
    return <Space>{display}</Space>;
  }

  return (
    <div data-bem="ReleaseInfoBoxRow__info">
      {!isUpToDate && (
        <>
          <p>{t('composite.sidebarMenu.releaseInfoBox.newVersionMsg')}</p>
          <br />
        </>
      )}
      <table data-bem="ReleaseInfoBoxRow__table">
        <tbody>
          <ReleaseInfoBoxRow
            name={t('composite.sidebarMenu.releaseInfoBox.upToDate')}
            value={isUpToDate ? t('composite.sidebarMenu.releaseInfoBox.yes') : t('composite.sidebarMenu.releaseInfoBox.no')}
          />
          <ReleaseInfoBoxRow name={t('composite.sidebarMenu.releaseInfoBox.display')} value={display} />
          <ReleaseInfoBoxBreak />
          <ReleaseInfoBoxRow name={t('composite.sidebarMenu.releaseInfoBox.build')} value={CONF.buildCiNum} />
          <ReleaseInfoBoxRow name={t('composite.sidebarMenu.releaseInfoBox.branch')} value={CONF.buildCiBranch} />
          <ReleaseInfoBoxRow name={t('composite.sidebarMenu.releaseInfoBox.tag')} value={CONF.buildCiTag} />
          <ReleaseInfoBoxRow name={t('composite.sidebarMenu.releaseInfoBox.currentHash')} value={CONF.buildShaLong} />
          <ReleaseInfoBoxRow name={t('composite.sidebarMenu.releaseInfoBox.shortenedCurrentHash')} value={CONF.buildShaShort} />
          <ReleaseInfoBoxRow name={t('composite.sidebarMenu.releaseInfoBox.currentReleaseTime')} value={CONF.buildNow} />
          <ReleaseInfoBoxBreak />
          <ReleaseInfoBoxRow name={t('composite.sidebarMenu.releaseInfoBox.nextHash')} value={nextSha} />
          <ReleaseInfoBoxRow name={t('composite.sidebarMenu.releaseInfoBox.nextReleaseTime')} value={nextNow} />
          <ReleaseInfoBoxBreak />
          <ReleaseInfoBoxRow name={t('composite.sidebarMenu.releaseInfoBox.environment')} value={CONF.environment} />
          <ReleaseInfoBoxRow name={t('composite.sidebarMenu.releaseInfoBox.rEnvironment')} value={CONF.reactEnv} />
          <ReleaseInfoBoxRow name={t('composite.sidebarMenu.releaseInfoBox.nEnvironment')} value={CONF.nodeEnv} />
        </tbody>
      </table>
    </div>
  );
};

export default ReleaseInfoBox;
